import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag, faGift, faUserGroup, faXmark } from "@fortawesome/free-solid-svg-icons";
import { promoService, referralService, formatCurrency } from "../../../services/api";
import styles from "./cartPage.module.css";

const PROMO_NOT_FOUND = "GL-PRM-001";

/** Texto del beneficio de un código que se canjea (no es descuento). */
function redeemBenefitText(data) {
  if (!data) return "";
  if (data.benefit_type === "GIFT_BALANCE") {
    return `${formatCurrency(data.benefit_amount || data.benefit_value)} de saldo de regalo`;
  }
  if (data.benefit_type === "FREE_TICKET") {
    return String(data.bonus_label || "Un boleto gratis").toLowerCase();
  }
  return data.benefit_type_display || "un beneficio";
}

/**
 * "¿Tienes un código?" — canasta de cupón del carrito.
 * Un solo campo que primero valida el código como promoción (POST /discounts/promo/validate/):
 * - CHECKOUT → descuento sobre la compra (lo aplica el padre y se envía como `promo_code`).
 * - REDEEM → saldo de regalo o boleto gratis: se ofrece "Canjear" (POST /promo/redeem/).
 * Si no existe como promoción y el usuario aún puede ser referido, se le pregunta si es el
 * código de un amigo (POST /discounts/referrals/apply/) — solo con su confirmación.
 *
 * @param {object} props
 * @param {boolean} props.isAuthenticated
 * @param {() => void} props.onRequireLogin
 * @param {object[]} props.rows        Canasta en forma create_bet (con bonus_id)
 * @param {number} props.eligibleAmount Valor de las apuestas que no van con bono
 * @param {object|null} props.promo     Código de descuento aplicado (estado del padre)
 * @param {(promo: object) => void} props.onApply
 * @param {() => void} props.onRemove
 * @param {(data: object) => void} props.onRedeemed  Tras canjear (refrescar saldo/bonos)
 * @param {boolean} props.canUseReferral  El usuario aún no hace su primera recarga
 */
export default function CouponBox({
  isAuthenticated,
  onRequireLogin,
  rows,
  eligibleAmount,
  promo,
  onApply,
  onRemove,
  onRedeemed,
  canUseReferral,
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: "success"|"error"|"info", text }
  const [pendingRedeem, setPendingRedeem] = useState(null); // datos de validate (REDEEM)
  const [askReferral, setAskReferral] = useState(""); // código a confirmar como referido

  const resetPrompts = () => {
    setPendingRedeem(null);
    setAskReferral("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = code.trim().toUpperCase();
    if (!value || busy) return;
    resetPrompts();
    setFeedback(null);

    if (!isAuthenticated) {
      setFeedback({ type: "info", text: "Inicia sesión para usar un código." });
      onRequireLogin?.();
      return;
    }

    setBusy(true);
    const res = await promoService.validate({ code: value, amount: eligibleAmount, bets: rows });
    setBusy(false);

    if (res.ok) {
      const data = res.data || {};
      if (data.applies_in === "REDEEM") {
        setPendingRedeem({ ...data, code: data.code || value });
        return;
      }
      onApply({ code: data.code || value, data, order: data.order || null });
      setCode("");
      setFeedback({ type: "success", text: res.message || `Código "${value}" aplicado.` });
      return;
    }

    if ((res.code === PROMO_NOT_FOUND || res.status === 404) && canUseReferral) {
      setAskReferral(value);
      return;
    }
    setFeedback({ type: "error", text: res.message });
  };

  const handleRedeem = async () => {
    if (!pendingRedeem || busy) return;
    setBusy(true);
    const res = await promoService.redeem(pendingRedeem.code);
    setBusy(false);
    if (res.ok) {
      setPendingRedeem(null);
      setCode("");
      setFeedback({ type: "success", text: res.message || "¡Listo! Canjeaste tu código." });
      onRedeemed?.(res.data || {});
      return;
    }
    setFeedback({ type: "error", text: res.message });
  };

  const handleApplyReferral = async () => {
    if (!askReferral || busy) return;
    setBusy(true);
    const res = await referralService.apply(askReferral);
    setBusy(false);
    setAskReferral("");
    if (res.ok) {
      setCode("");
      const name = res.data?.referrer_name;
      setFeedback({
        type: "success",
        text:
          res.message ||
          `¡Listo! Quedaste registrado como referido${name ? ` de ${name}` : ""}. Haz tu primera recarga para activar los beneficios.`,
      });
      return;
    }
    setFeedback({ type: "error", text: res.message });
  };

  const discount = Number(promo?.order?.discount_amount ?? promo?.data?.benefit_amount);

  return (
    <div className={styles.couponBox}>
      {promo ? (
        <div
          className={`${styles.couponApplied} ${promo.error ? styles.couponAppliedError : ""}`}
        >
          <FontAwesomeIcon icon={faTag} className={styles.couponAppliedIcon} />
          <div className={styles.couponAppliedText}>
            <strong>{promo.code}</strong>
            <span>
              {promo.error
                ? promo.error
                : promo.checking
                  ? "Recalculando el descuento…"
                  : Number.isFinite(discount) && discount > 0
                    ? `Descuento de ${formatCurrency(discount)} aplicado`
                    : promo.data?.campaign || "Código aplicado"}
            </span>
          </div>
          <button
            type="button"
            className={styles.couponRemove}
            onClick={() => {
              onRemove();
              setFeedback(null);
            }}
            aria-label={`Quitar el código ${promo.code}`}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            className={styles.couponToggle}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            <FontAwesomeIcon icon={faTag} /> ¿Tienes un código?
          </button>

          {open && (
            <form className={styles.couponForm} onSubmit={handleSubmit}>
              <label htmlFor="cart-coupon" className={styles.srOnly}>
                Código promocional o de referido
              </label>
              <input
                id="cart-coupon"
                className={styles.couponInput}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase().replace(/\s+/g, ""));
                  setFeedback(null);
                  resetPrompts();
                }}
                placeholder="Código promocional o de un amigo"
                maxLength={30}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              <button
                type="submit"
                className={styles.couponApplyBtn}
                disabled={busy || !code.trim()}
              >
                {busy ? "Validando…" : "Aplicar"}
              </button>
            </form>
          )}
        </>
      )}

      {pendingRedeem && (
        <div className={styles.couponPrompt}>
          <p>
            <FontAwesomeIcon icon={faGift} /> El código <strong>{pendingRedeem.code}</strong> te
            da <strong>{redeemBenefitText(pendingRedeem)}</strong>. No es un descuento: canjéalo
            para recibirlo.
          </p>
          <div className={styles.couponPromptActions}>
            <button
              type="button"
              className={styles.couponApplyBtn}
              onClick={handleRedeem}
              disabled={busy}
            >
              {busy ? "Canjeando…" : "Canjear"}
            </button>
            <button
              type="button"
              className={styles.couponGhostBtn}
              onClick={() => setPendingRedeem(null)}
              disabled={busy}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {askReferral && (
        <div className={styles.couponPrompt}>
          <p>
            <FontAwesomeIcon icon={faUserGroup} /> No encontramos una promoción con el código{" "}
            <strong>{askReferral}</strong>. ¿Es un código de referido de un amigo?
          </p>
          <div className={styles.couponPromptActions}>
            <button
              type="button"
              className={styles.couponApplyBtn}
              onClick={handleApplyReferral}
              disabled={busy}
            >
              {busy ? "Registrando…" : "Sí, es de un amigo"}
            </button>
            <button
              type="button"
              className={styles.couponGhostBtn}
              onClick={() => {
                setAskReferral("");
                setFeedback({ type: "error", text: `El código "${askReferral}" no existe.` });
              }}
              disabled={busy}
            >
              No
            </button>
          </div>
        </div>
      )}

      {feedback && (
        <p
          className={
            feedback.type === "error"
              ? styles.couponError
              : feedback.type === "success"
                ? styles.couponSuccess
                : styles.couponInfo
          }
          role={feedback.type === "error" ? "alert" : "status"}
        >
          {feedback.text}
        </p>
      )}
    </div>
  );
}
